<template>
  <div
    v-if="shouldShowLogo && !errored"
    class="team-logo"
    :style="`--logo-size:${px};`"
  >
    <img
      :src="src"
      :alt="`${props.participant.shortName} logo`"
      loading="lazy"
      decoding="async"
      @error="onError"
    />
  </div>
</template>

<script setup>
import { computed, ref, watch, watchEffect } from 'vue';
import { getTeamsForSport } from '@/utils/teamLoader';
import { useUserStore } from '@/stores/user';

const userStore = useUserStore();

/**
 * Mirror of the normalization that `lookupTeamDisplay` in
 * @/consts/teamsDisplayNames does, applied here against the sport-specific
 * team data loaded lazily by `getTeamsForSport`. Keeps period-prefixed
 * leagues like F5-MLB / 1H-NBA resolving to the underlying team entry.
 */
const PERIOD_PREFIX_RE = /^(?:F5|1H|2H|Q[1-4])\s+/;
const SMART_QUOTE_RE = /[\u2018\u2019\u02BC\uFF07]/g;
function normalizeTeamLookupKey(name) {
  if (!name || typeof name !== 'string') return name;
  return name.replace(SMART_QUOTE_RE, "'").trim().replace(PERIOD_PREFIX_RE, '');
}

function escapeRegExp(str) {
  return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// Team-data keys are ASCII (e.g. 'Nicolai Hojgaard') but participant names
// can carry diacritics ("Nicolai Højgaard Wins"). NFD strips combining marks
// (é → e); the map covers letters NFD can't decompose (ø, æ, ß, …).
const NON_DECOMPOSING_MAP = {
  ø: 'o',
  Ø: 'O',
  æ: 'ae',
  Æ: 'AE',
  œ: 'oe',
  Œ: 'OE',
  ß: 'ss',
  đ: 'd',
  Đ: 'D',
  ł: 'l',
  Ł: 'L',
};
function foldDiacritics(str) {
  return str
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[øØæÆœŒßđĐłŁ]/g, (c) => NON_DECOMPOSING_MAP[c]);
}

const props = defineProps({
  participant: {
    type: Object,
    required: true,
  },
  sport: {
    type: String,
    required: true,
  },
  size: {
    type: Number,
    default: 28,
  },
  // New prop to control validation behavior
  validationMode: {
    type: String,
    default: 'auto', // 'auto' (check team data), 'hide' (never show), 'show' (always show)
    validator: (value) => {
      return ['auto', 'hide', 'show'].includes(String(value));
    },
  },
  // Optional prop to require both participants to be valid (for game contexts)
  requireBothParticipants: {
    type: Boolean,
    default: false,
  },
  // Optional second participant for validation
  secondParticipant: {
    type: Object,
    default: null,
  },
  // "Winner" outright markets (e.g. SOCCER-WINNER) have yes/no participants like
  // "Argentina win World Cup" / "Does NOT Win". When set, derive the subject
  // team/country from inside the participant name and show its flag on both
  // sides, ignoring the usual yes/no logo suppression.
  winnerMarket: {
    type: Boolean,
    default: false,
  },
});

const baseURL = 'https://cdn.4casters.io/';
const errored = ref(false);
const onError = () => {
  // Hide broken image if CDN asset missing
  errored.value = true;
};
// Reactive team data
const teamData = ref(null);
const isLoading = ref(false);

// Load team data for the specific sport
watchEffect(async () => {
  if (!props.sport) return;

  isLoading.value = true;
  try {
    teamData.value = await getTeamsForSport(props.sport);
  } catch (error) {
    console.warn(`Failed to load team data for sport: ${props.sport}`, error);
    teamData.value = {};
  } finally {
    isLoading.value = false;
  }
});

/**
 * Real teams whose shortName is literally 'NO' or 'YES', which would otherwise
 * be read as the no/yes side of a yes/no market and have their logo suppressed.
 * Keyed by normalized longName, so the period-prefixed variants (1H/2H New
 * Orleans Saints) are covered by the same entry.
 *
 * Deliberately an explicit list rather than "does this resolve in teamData?" —
 * ~55 golfers have yes/no futures legs whose longName IS their team-data key
 * (e.g. 'Scottie Scheffler' with shortName 'YES'), so a teamData-based check
 * would start showing headshots on both sides of those markets.
 */
const YES_NO_SHORTNAME_COLLISIONS = new Set([
  'New Orleans Saints', // NFL, shortName 'NO'
  'New Orleans', // NCAA Privateers, shortName 'NO'
]);

const yesNoSide = computed(() => {
  const futuresSide = props.participant?.futuresSide;
  if (typeof futuresSide === 'string') {
    const upper = futuresSide.toUpperCase();
    if (upper === 'YES') return 'yes';
    if (upper === 'NO') return 'no';
  }
  // A genuine yes/no leg carries futuresSide (checked above). Past that point
  // the shortName heuristic is a fallback, so exempt the known collisions.
  if (
    YES_NO_SHORTNAME_COLLISIONS.has(
      normalizeTeamLookupKey(props.participant?.longName),
    )
  ) {
    return null;
  }
  const shortName = props.participant?.shortName;
  if (typeof shortName === 'string') {
    const upper = shortName.toUpperCase();
    // Suffix only: NFL Saints use shortName "NO", which is not a yes/no market.
    if (upper.endsWith('-YES')) return 'yes';
    if (upper.endsWith('-NO')) return 'no';
  }
  return null;
});
// For "Winner" outright markets, locate the team/country whose name appears
// inside the participant name (e.g. "Argentina" within "Argentina win World
// Cup"). Falls back to the sibling participant so the "Does NOT Win" side shows
// the same subject flag.
function findTeamKeyInName(name) {
  if (!teamData.value || !name || typeof name !== 'string') return null;
  const normalized = normalizeTeamLookupKey(name);
  if (teamData.value[normalized]) return normalized;
  // Compare diacritic-folded forms so "Nicolai Højgaard Wins" still finds the
  // ASCII-keyed 'Nicolai Hojgaard' entry. The original key is returned for the
  // teamData lookup.
  const folded = foldDiacritics(normalized);
  let best = null;
  for (const key of Object.keys(teamData.value)) {
    if (key.length < 3) continue;
    const re = new RegExp(`\\b${escapeRegExp(foldDiacritics(key))}\\b`, 'i');
    if (re.test(folded) && (!best || key.length > best.length)) {
      best = key;
    }
  }
  return best;
}

const winnerTeamKey = computed(() => {
  if (!props.winnerMarket || !teamData.value) return null;
  return (
    findTeamKeyInName(props.participant?.longName) ||
    findTeamKeyInName(props.secondParticipant?.longName)
  );
});

const shouldShowLogo = computed(() => {
  if (userStore.user?.username?.toLowerCase() === 'george') return false;

  // Winner markets: show the derived subject flag (bypasses yes/no suppression).
  if (props.winnerMarket) return !!winnerTeamKey.value;

  // yes/no markets intentionally render no logo — the text (e.g. "OKC YES") carries the side.
  if (yesNoSide.value) return false;

  if (props.validationMode === 'show') return true;
  if (props.validationMode === 'hide') return false;

  // Auto mode - check if team data is available
  if (!teamData.value) return false;

  const longName = normalizeTeamLookupKey(props.participant.longName);
  // Check if current participant has team data
  const hasCurrentTeamData = teamData.value[longName];

  // If requireBothParticipants is true, also check second participant
  if (props.requireBothParticipants && props.secondParticipant) {
    const secondLongName = normalizeTeamLookupKey(
      props.secondParticipant.longName,
    );
    const hasSecondTeamData = teamData.value[secondLongName];
    return hasCurrentTeamData && hasSecondTeamData;
  }

  return hasCurrentTeamData;
});

const logoPath = computed(() => {
  // Winner markets resolve the flag from the derived subject team/country.
  if (
    props.winnerMarket &&
    winnerTeamKey.value &&
    teamData.value?.[winnerTeamKey.value]?.logoUrl
  ) {
    return teamData.value[winnerTeamKey.value].logoUrl;
  }

  const longName = normalizeTeamLookupKey(props.participant.longName);
  // Check if we have team data and the specific team has a logoUrl
  if (
    teamData.value &&
    teamData.value[longName] &&
    teamData.value[longName].logoUrl
  ) {
    return teamData.value[longName].logoUrl;
  }

  // Fallback to convention-based naming
  return `${props.sport}_${props.participant.shortName}.webp`;
});
const isFlag = computed(() => logoPath.value.startsWith('flags/'));
const px = computed(
  () => `${Math.round(props.size * (isFlag.value ? 0.9 : 1))}px`,
);
const src = computed(() => `${baseURL}${logoPath.value}`);

// `errored` latches on the first failed load. In 'show' mode the logoPath can
// briefly resolve to the convention fallback before the sport's team data
// finishes loading; without resetting on src change, that transient 404 would
// permanently hide an otherwise-valid logo. Reset whenever the source changes.
watch(src, () => {
  errored.value = false;
});
</script>

<style scoped lang="scss">
@use '@/scss/tokens.scss' as main;

.team-logo {
  display: inline-block;
  align-items: center;
  justify-content: center;
  width: var(--logo-size);
  height: var(--logo-size);
  padding: 3px;
  img {
    width: 100%;
    vertical-align: middle;
  }
}
</style>
